<!-- A specific Ticker, showing its message, preview and use on Decoders. -->

<template>
  <div>
    <navbar :links="links" />
    <v-list
      v-if="ticker.id"
      dense
      avatar
    >
      <v-list-tile
        avatar
        :ripple="false"
        class="no-hover-clr no-click-pointer"
      >
        <v-list-tile-avatar>
          <v-icon
            large
            class="msr--text"
          >
            textsms
          </v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content v-if="ticker.id">
          <v-list-tile-title>
            <strong
              class="grey--text text--darken-3"
              style="white-space: initial"
            >{{ ticker.message }}</strong>
          </v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>
    <!-- Preview -->
    <v-layout
      row
      justify-center
    >
      <ticker-preview
        v-if="ticker.id"
        :message="ticker.message"
      />
    </v-layout>
    <!-- Group, Author and full Message -->
    <v-layout
      v-bind="layout"
      class="mt-3"
    >
      <v-text-field
        v-if="ticker.id && ticker.group"
        disabled
        class="v-text-custom-disabled mr-2"
        :label="$t('group')"
        hide-details
        :value="ticker.group.name"
      />
      <v-layout
        align-center
        class="grow-1"
        :class="{'mt-2': layout.column}"
      >
        <v-text-field
          v-if="ticker.id && ticker.creator"
          disabled
          class="v-text-custom-disabled"
          :label="$t('author')"
          hide-details
          :value="ticker.creator.first_name + ' ' + ticker.creator.last_name"
        />
        <v-layout
          align-center
          class="ticker-detail-toolbar"
        >
          <!-- Edit Ticker -->
          <v-tooltip
            bottom
            style="cursor: help"
          >
            <v-btn
              v-if="!loading && ticker.id && !ticker.read_only"
              slot="activator"
              color="msr--text"
              icon
              class="ma-0"
              @click="showEditDialog = true"
            >
              <v-icon medium>
                edit
              </v-icon>
            </v-btn>
            <span>
              {{ $t('editTicker') }}
            </span>
          </v-tooltip>
          <!-- Delete Ticker -->
          <v-tooltip
            bottom
            style="cursor: help"
          >
            <v-btn
              v-if="!loading && ticker.id && !ticker.read_only"
              slot="activator"
              color="msr--text"
              icon
              class="ma-0"
              @click="showRemoveTickerDialog = true"
            >
              <v-icon medium>
                delete
              </v-icon>
            </v-btn>
            <span>
              {{ $t('deleteTicker') }}
            </span>
          </v-tooltip>
          <!-- Batch Actions Menu -->
          <v-menu
            v-if="ticker.id && entries.length"
            offset-y
            left
          >
            <v-btn
              slot="activator"
              color="grey--text text--darken-1"
              icon
              class="ma-0"
            >
              <v-icon
                class="hoverable-icon"
                medium
              >
                more_vert
              </v-icon>
            </v-btn>
            <v-list>
              <v-list-tile>
                <v-list-tile-title>
                  <strong
                    class="msr--text"
                  >{{ $t('forMultiple') + ' ' + $t('decoders') }} ...</strong>
                </v-list-tile-title>
              </v-list-tile>
              <v-divider />
              <v-list-tile
                @click="
                  expandAllEntries();
                  entrySelectionOverlay.toggle('timing');
                "
              >
                <v-list-tile-title class="menu-item">
                  <v-icon
                    class="mr-2"
                    :class="{
                      'grey--text': !allEntriesUseTiming(entries),
                      'msr--text': allEntriesUseTiming(entries),
                    }"
                  >
                    timer
                  </v-icon>
                  {{ $t('timing') }}
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                @click="
                  expandAllEntries();
                  entrySelectionOverlay.toggle('removing');
                "
              >
                <v-list-tile-title class="menu-item">
                  <v-icon class="grey--text mr-2">
                    block
                  </v-icon>
                  {{ $t('deactivate') }}
                </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-layout>
      </v-layout>
    </v-layout>
    <blockquote
      v-if="ticker.id"
      class="subheading msr--text mt-2"
    >
      <v-icon
        small
        class="msr--text"
      >
        format_quote
      </v-icon>
      <span
        class="clickable"
        :title="$t('editTicker')"
        @click="showEditDialog = true"
      >{{ ticker.message }}</span>
      <v-icon
        small
        class="msr--text"
        style="transform: rotate(180deg)"
      >
        format_quote
      </v-icon>
    </blockquote>
    <!-- Loading indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- 'No entries' -->
    <no-entries-header
      :show="!!ticker.id && !entries.length"
      :label="$t('noTickerEntries')"
      :show-activation-link="!!ticker.id && !entrySelectionOverlay.active"
      :on-link-clicked="() => goToAddDecoderToTicker($route.params.tickerId)"
    />
    <!-- Active Content -->
    <ticker-detail-entries
      v-if="ticker.id"
      ref="activeTickerDetailEntries"
      class="mt-2"
      :entries="entries"
      active-only
      :show-selection-checkboxes="entrySelectionOverlay.active"
      show-header
      @entry-deleted="fetchTicker()"
      @changed="fetchTicker()"
    />
    <!-- Future Content -->
    <ticker-detail-entries
      v-if="ticker.id"
      ref="futureTickerDetailEntries"
      class="mt-2"
      :entries="entries"
      future-only
      :show-selection-checkboxes="entrySelectionOverlay.active"
      show-header
      @entry-deleted="fetchTicker()"
      @changed="fetchTicker()"
    />
    <!-- Add Decoder to Ticker button -->
    <v-slide-y-reverse-transition>
      <v-btn
        v-if="ticker.id && !entrySelectionOverlay.active"
        :key="ticker.id"
        class="msr"
        :class="$desktopModeStyles"
        fixed
        bottom
        right
        dark
        round
        @click="goToAddDecoderToTicker($route.params.tickerId)"
      >
        <v-icon left>
          add
        </v-icon>
        {{ $t('addDecoderToTicker') }}
      </v-btn>
    </v-slide-y-reverse-transition>
    <!-- Empty placeholder to allow a bit more scrolling -->
    <div style="height: 100px" />
    <!-- Remove-Ticker Dialog -->
    <v-dialog
      v-model="showRemoveTickerDialog"
      :width="$store.state.common.largeDialogWidth"
    >
      <v-card>
        <v-card-title class="title pb-0">
          <v-layout align-center>
            {{ $t('deleteTicker') }}
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="showRemoveTickerDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text>
          <div class="subheading">
            {{ removalQuestion }}
            <div class="error--text mt-2 pa-2">
              <strong>{{ $t('unpublishContentHint_2') }}</strong>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            @click="removeTicker(ticker)"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Edit Dialog -->
    <v-dialog
      v-if="ticker.id"
      v-model="showEditDialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      transition="dialog-bottom-transition"
      absolute
      persistent
      :width="$store.state.common.largeDialogWidth"
    >
      <v-card>
        <v-card-title class="title pb-0">
          <v-layout align-center>
            {{ $t('editTicker') }}
            <v-btn
              class="ml-auto mr-0 my-0"
              flat
              icon
              :title="$t('close')"
              @click="abortEditTicker()"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="newTickerMessage"
            clearable
            :label="$t('message')"
            :counter="messageLimit"
            :rules="[rules.messageMin, rules.messageMax]"
            autofocus
            @keyup.native.ctrl.enter="editTicker(ticker)"
            @keyup.native.esc="abortEditTicker()"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            :disabled="!newMessageIsValid"
            @click="editTicker(ticker)"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Entry Selection Overlay for Batch Timing -->
    <entry-selection-overlay
      v-if="entries"
      :show="entrySelectionOverlay.active"
      :entries="entries"
      :mode="entrySelectionOverlay.mode"
      :title="entrySelectionOverlay.title"
      :title-icon="entrySelectionOverlay.titleIcon"
      :loading="loading"
      @aborted="entrySelectionOverlay.stop()"
      @confirmed="
        entrySelectionOverlay.mode === 'timing'
          ? (showEntryTimingDialog = true)
          : batchRemoveEntries(selectedEntries)
      "
    />
    <!-- Entry Timing Dialog -->
    <entry-timing-dialog
      :show.sync="showEntryTimingDialog"
      :entries="selectedEntries"
      :show-timeframe-controls="false"
      @submitted="
        entrySelectionOverlay.stop();
        fetchTicker();
      "
    />
  </div>
</template>

<script>
import commonTicker from '@/mixins/commonTicker.js';
import entrySelectionOverlay from '@/mixins/entrySelectionOverlay.js';
import identifiers from '@/mixins/identifiers.js';

import BtnOk from '@/components/ButtonOk';
import EntrySelectionOverlay from '@/components/EntrySelectionOverlay';
import EntryTimingDialog from '@/components/EntryTimingDialog';
import Navbar from '@/components/Navbar';
import NoEntriesHeader from '@/components/NoEntriesHeader';
import TickerDetailEntries from '@/components/TickerDetailEntries';
import TickerPreview from '@/components/TickerPreview';

export default {
  components: {
    BtnOk,
    EntrySelectionOverlay,
    EntryTimingDialog,
    Navbar,
    NoEntriesHeader,
    TickerDetailEntries,
    TickerPreview,
  },
  mixins: [commonTicker, entrySelectionOverlay, identifiers],
  data() {
    return {
      links: ['contents', {label: 'ticker', active: false}],
      loading: false,
      ticker: {
        entries: [],
      },
      showRemoveTickerDialog: false,
      showEntryTimingDialog: false,
      showEditDialog: false,
      newTickerMessage: '',
      messageLimit: 400,
      rules: {
        messageMin: (value) => {
          return (
            (value && value.trim().length > 0) || this.$t('messageTooShort')
          );
        },
        messageMax: (value) => {
          return (
            (value && value.length <= this.messageLimit) ||
            this.$t('messageTooLong')
          );
        },
      },
    };
  },
  computed: {
    layout() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return {column: true};
      }
      return {row: true};
    },
    entries() {
      return this.ticker.entries;
    },
    removalQuestion() {
      if (!this.ticker) {
        return '';
      }
      return this.$t('askDeleteTicker').replace(
        '{{ num }}',
        this.entries.length
      );
    },
    newMessageIsValid() {
      return (
        this.newTickerMessage !== this.ticker.message &&
        this.newTickerMessage.length <= this.messageLimit &&
        this.newTickerMessage.length > 0
      );
    },
  },
  watch: {
    newTickerMessage: function (message) {
      if (message === null) {
        this.newTickerMessage = '';
      }
    },
  },
  mounted() {
    this.entrySelectionOverlay.view = 'content';

    this.fetchTicker().then((response) => {
      this.updateNavbarLinks();
    });
  },
  methods: {
    /**
     * Call this before anything using entry checkboxes is called.
     * It will ensure the StoreWrappers are expanded so checkboxes are
     * visible to the user.
     */
    expandAllEntries() {
      this.$refs.activeTickerDetailEntries.allExpanded = true;
      this.$refs.futureTickerDetailEntries.allExpanded = true;
    },
    alertReadOnly() {
      window.alert(this.$t('noWriteAccess'));
    },
    fetchTicker() {
      this.loading = true;
      const promise = this.$api.retrieveUserTicker(this.$route.params.tickerId);
      promise.then((response) => {
        this.ticker = response.data;
        this.newTickerMessage = this.ticker.message;
        this.loading = false;
      });
      return promise;
    },
    updateNavbarLinks() {
      let tickerLink = {label: this.ticker.message, active: false};
      this.links = ['contents', tickerLink];

      let previous = this.$store.getters.previousRoute;
      if (!previous) {
        return;
      }

      if (previous.name === 'contents') {
        this.links = ['contents', tickerLink];
      } else if (previous.name === 'dashboard') {
        this.links = ['dashboard', tickerLink];
      } else if (previous.name === 'decoder') {
        this.$api
          .retrieveDecoder(previous.params.ctype, previous.params.decoderId)
          .then((response) => {
            const decoder = response.data;
            const decoderLink = {
              label: decoder.description,
              path: previous.path,
            };
            this.links = ['sites', decoderLink, tickerLink];
          });
      }
    },
    removeTicker(ticker) {
      let vm = this;
      vm.$api.deleteTicker(ticker.id).then((response) => {
        vm.$emit('deleted');
        vm.$bus.$emit('alert-success', {
          message: 'allEntriesDeletedSuccess',
          timeout: 2000,
          translate: true,
        });
        this.$store.commit('REMOVE_CONTENT_IN_LISTS', ticker);
        vm.$router.push('/content');
      });
    },
    batchRemoveEntries(entries) {
      this.loading = true;
      const allowedEntries = null;
      this.batchRemoveMultipleEntries(entries, allowedEntries).then(() => {
        this.loading = false;
        this.entrySelectionOverlay.stop();
        this.fetchTicker();
      });
    },
    editTicker(ticker) {
      let vm = this;
      let payload = {message: this.newTickerMessage};

      vm.showEditDialog = false;
      vm.$nextTick(() => {
        vm.$api.updateTicker(ticker.id, payload).then((response) => {
          vm.$emit('updated');
          vm.$bus.$emit('alert-success', {
            message: 'entryUpdatedSuccess',
            timeout: 2000,
            translate: true,
          });

          vm.fetchTicker();
        });
      });
    },
    abortEditTicker() {
      this.newTickerMessage = this.ticker.message;
      this.showEditDialog = false;
    },
    goToAddDecoderToTicker(tickerId) {
      const path = '/ticker/' + tickerId + '/add';
      this.$router.push(path);
    },
  },
};
</script>

<style scoped>
.ticker-detail-toolbar > *:not(:last-child) {
  margin-right: 8px !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/TickerDetail.vue